/* eslint-disable id-length */
'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type RefObject, useRef, useState } from 'react';
import { centerCrop, type Crop, makeAspectCrop, type PercentCrop, type PixelCrop, ReactCrop } from 'react-image-crop';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import styles from './profile-photo-cropper.module.scss';
type ProfilePhotoCropperProps = {
  aspect?: number;
  circular?: boolean;
  className?: string;
  cropAriaLiveText?: string;
  imgRef: RefObject<HTMLImageElement | null>;
  onCropChange: (crop: PixelCrop) => void;
  src: string | undefined;
};
const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
  return centerCrop(makeAspectCrop({
    unit: '%',
    width: 100
  }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
};
const percentCropToPixelCrop = (percentCrop: PercentCrop, image: HTMLImageElement): PixelCrop => {
  const width = image.width;
  const height = image.height;
  return {
    height: percentCrop.height / 100 * height,
    unit: 'px',
    width: percentCrop.width / 100 * width,
    x: percentCrop.x / 100 * width,
    y: percentCrop.y / 100 * height
  };
};
export const ProfilePhotoCropper = t0 => {
  const $ = _c(27);
  const {
    aspect: t1,
    circular: t2,
    className,
    cropAriaLiveText,
    imgRef,
    onCropChange,
    src
  } = t0;
  const aspect = t1 === undefined ? 1 : t1;
  const circular = t2 === undefined ? true : t2;
  const [crop, setCrop] = useState();
  const [ariaLiveText, setAriaLiveText] = useState("");
  const formatTranslationString = useFormatTranslationString();
  const debounceTimeout = useRef(null);
  let t3;
  if ($[0] !== aspect || $[1] !== onCropChange) {
    t3 = event => {
      const {
        height,
        width
      } = event.currentTarget;
      const centeredPercentCrop = centerAspectCrop(width, height, aspect);
      const centeredPixelCrop = percentCropToPixelCrop(centeredPercentCrop, event.currentTarget);
      setCrop(centeredPercentCrop);
      onCropChange(centeredPixelCrop);
    };
    $[0] = aspect;
    $[1] = onCropChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleImageLoad = t3;
  let t4;
  if ($[3] !== cropAriaLiveText || $[4] !== formatTranslationString) {
    t4 = (pixelCrop, percentCrop) => {
      setCrop(percentCrop);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (cropAriaLiveText) {
        debounceTimeout.current = setTimeout(() => {
          setAriaLiveText(formatTranslationString(cropAriaLiveText, {
            positionX: pixelCrop.x.toFixed(2),
            positionY: pixelCrop.y.toFixed(2),
            sizeHeight: percentCrop.height.toFixed(2),
            sizeWidth: percentCrop.width.toFixed(2)
          }));
        }, 100);
      }
    };
    $[3] = cropAriaLiveText;
    $[4] = formatTranslationString;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleCropChange = t4;
  let t5;
  if ($[6] !== ariaLiveText) {
    t5 = <div aria-live="polite" className={styles["aria-live-text"]}>{ariaLiveText}</div>;
    $[6] = ariaLiveText;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== className) {
    t6 = classNames(styles.wrapper, className);
    $[8] = className;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = aspect ?? 1;
  const t8 = circular ?? true;
  let t9;
  if ($[10] !== handleImageLoad || $[11] !== imgRef || $[12] !== src) {
    t9 = src && <img alt="Crop preview" className={styles.cropper__image} onLoad={handleImageLoad} ref={imgRef} src={src} />;
    $[10] = handleImageLoad;
    $[11] = imgRef;
    $[12] = src;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== crop || $[15] !== handleCropChange || $[16] !== onCropChange || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <ReactCrop aspect={t7} circularCrop={t8} className={styles.cropper} crop={crop} onChange={handleCropChange} onComplete={onCropChange}>{t9}</ReactCrop>;
    $[14] = crop;
    $[15] = handleCropChange;
    $[16] = onCropChange;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <div className={t6} id="profile-photo-cropper" role="application">{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t5) {
    t12 = <>{t5}{t11}</>;
    $[24] = t11;
    $[25] = t5;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};